import {
  Button,
  Flex,
  IconButton,
  useDisclosure,
  Text,
  Image,
  Box,
} from "@chakra-ui/react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import MuiIcon from "components/MuiIcon";
import { useEffect, useMemo, useState } from "react";
import { FileT, TemplateRequestT, TemplateT } from "shared/api/types/channels";
import Table from "shared/components/table/Table";
import useGetColors from "shared/hooks/useGetColors";
import { formatDateToHuman } from "shared/utils/formattings";
import DeleteModal from "../admin/DeleteModal";
import { useTranslation } from "react-i18next";
import {
  useCreateTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
} from "shared/api/requests/channels";
import { useParams } from "react-router-dom";
import { PathsParams, PathsE } from "shared/routes";
import TemplateModal from "./TemplateModal";
import { CreateTemplateFormT } from "shared/form-values/channels";
import {
  useDeleteFile,
  useGetOneFile,
  useSendFiles,
} from "shared/api/requests/files";

interface Props {
  values: TemplateT[];
}

const ChannelTemplates = ({ values }: Props) => {
  const { t } = useTranslation();
  const { channelId } = useParams<PathsParams[PathsE.SHOW_CHANNEL]>();
  const columnHelper = createColumnHelper<TemplateT>();
  const { brand500 } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenTemplateModal,
    onOpen: onOpenTemplateModal,
    onClose: onCloseTemplateModal,
  } = useDisclosure();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedTemplateFiles, setSelectedTemplateFiles] = useState<FileT[]>(
    [],
  );
  const [fileIdsToDelete, setFileIdsToDelete] = useState<string[]>([]);

  const { mutateAsync: sendFilesMutation } = useSendFiles({
    pathParams: { sourceId: channelId },
    params: {},
  });

  const { mutate: updateMutation, isPending: isUpdating } = useUpdateTemplate({
    pathParams: { channelId, pushId: selectedTemplate! },
    params: undefined,
  });

  const { mutate: createMutation, isPending: isCreating } = useCreateTemplate({
    pathParams: { channelId },
    params: undefined,
  });

  const { mutate: deleteMutation, isPending: isDeleting } = useDeleteTemplate({
    pathParams: { pushId: selectedTemplate!, channelId },
    params: undefined,
  });

  const { mutateAsync: getOneFileMutation } = useGetOneFile();

  const { mutate: deleteFileMutation, isPending: isDeletingFile } = useDeleteFile();

  const getFileUrl = async (files: FileT[]) => {
    const changeFileUrl = async (file: FileT) => {
      const res = await getOneFileMutation({ fileId: file.id });
      const url = URL.createObjectURL(res);
      return { ...file, url };
    };

    return Promise.all(files.map(changeFileUrl));
  };

  useEffect(() => {
    const templateFiles = values.find(
      (template) => template.id === selectedTemplate,
    )?.files as unknown as FileT[] | undefined;

    (async () => {
      setSelectedTemplateFiles(await getFileUrl(templateFiles ?? []));
    })();
  }, [values, selectedTemplate]);

  const handleEdit = async (value: string) => {
    setSelectedTemplate(value);
    onOpenTemplateModal();
  };

  const handleFileDelete = (file: FileT) => {
    setSelectedTemplateFiles((prev) => prev.filter((f) => f.id !== file.id));
    URL.revokeObjectURL(file.url);
    setFileIdsToDelete((prev) => [...prev, file.id]);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("type", {
        header: t("channels.table.type"),
        id: "type",
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: "100px",
            },
          },
        },
      }),
      columnHelper.accessor("afterTime", {
        header: t("channels.table.afterTime"),
        id: "afterTime",
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: "100px",
            },
          },
        },
      }),
      columnHelper.accessor("message", {
        header: t("channels.table.message"),
        id: "message",
      }),
      columnHelper.accessor("createdAt", {
        header: t("subscribers.table.createdAt"),
        id: "createdAt",
        cell: ({ getValue }) => formatDateToHuman(getValue(), false),
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: "120px",
            },
          },
        },
      }),
      columnHelper.accessor("id", {
        header: "",
        id: "actions",
        cell: ({ getValue }) => (
          <Flex alignItems="center">
            <IconButton
              aria-label="edit-icon"
              icon={<MuiIcon name="edit" color={brand500} />}
              onClick={() => handleEdit(getValue())}
            />
            <IconButton
              aria-label="delete-icon"
              icon={<MuiIcon name="delete" color="#bb2124" />}
              onClick={() => {
                setSelectedTemplate(getValue());
                onOpen();
              }}
            />
          </Flex>
        ),
        meta: {
          styles: {
            sizes: {
              width: "100px",
            },
          },
        },
      }),
    ],
    [t],
  );

  const handleCloseTemplateModal = () => {
    onCloseTemplateModal();
    setSelectedTemplate(null);

    selectedTemplateFiles.forEach((file) => URL.revokeObjectURL(file.url));
    setSelectedTemplateFiles([]);
    setFileIdsToDelete([]);
  };

  const formatRequestData = (data: TemplateRequestT): TemplateRequestT => {
    if (!data.fileIds.length) delete data.fileIds;
    return {
      ...data,
      buttons: data.buttons,
      afterTime: Number(data.afterTime),
    };
  };

  const renderAddPushButton = () => (
    <Flex justifyContent="end">
      <Button
        my="10px"
        fontSize="16px"
        color="white"
        backgroundColor="brand.500"
        fontWeight="600"
        _hover={{
          backgroundColor: "brand.400",
        }}
        paddingY="8px"
        display="flex"
        borderRadius="8px"
        alignItems="center"
        onClick={onOpenTemplateModal}
      >
        <MuiIcon name="add" size={24} color="gray800" variant="rounded" />
        <Text ml="8px">{t("channels.push")}</Text>
      </Button>
    </Flex>
  );

  const ImagesViewer = useMemo(() => {
    return (
      <Flex my={2} gap={2} justifyContent={"center"} flexWrap={"wrap"}>
        {selectedTemplateFiles.map((f) => (
          <Flex key={f.id}>
            <Image
              sx={{
                borderRadius: "10px",
                objectFit: "cover",
                boxShadow: "-2px 8px 10px 0 rgba(0, 0, 0, 0.2)",
              }}
              src={f.url}
              alt={f.name}
              w={150}
              h={150}
            />
            <Box>
              <IconButton
                aria-label="delete-icon"
                onClick={() => handleFileDelete(f)}
              >
                <MuiIcon name="delete" />
              </IconButton>
            </Box>
          </Flex>
        ))}
      </Flex>
    );
  }, [selectedTemplateFiles, handleFileDelete]);

  const handleTemplateSubmit = async ({
    files,
    ...data
  }: CreateTemplateFormT) => {
    let fileIds = [] as string[];
    if (files.length) {
      const fileResponse = await sendFilesMutation(files);
      fileIds = fileResponse.map((file) => file.id);
    }

    selectedTemplateFiles.forEach(({ id }) => {
      if (!fileIdsToDelete.includes(id)) fileIds.push(id);
    });

    if (!selectedTemplate) {
      createMutation(formatRequestData({ ...data, fileIds }), {
        onSuccess: handleCloseTemplateModal,
      });
      return;
    }
    updateMutation(formatRequestData({ ...data, fileIds }), {
      onSuccess: handleCloseTemplateModal,
    });

    if (fileIdsToDelete.length) {
      fileIdsToDelete.forEach((fileId) => deleteFileMutation({ fileId }));
    }
  };

  return (
    <>
      {renderAddPushButton()}
      <Table
        data={values}
        columns={(columns as ColumnDef<TemplateT>[]) ?? []}
      />
      <DeleteModal
        title={t("channels.deletePush")}
        text={t("general.approveTo", { phrase: t("channels.deletePush") })}
        onClose={onClose}
        isOpen={isOpen}
        onSubmit={() =>
          deleteMutation({}, { onSuccess: () => setSelectedTemplate(null) })
        }
      />
      <TemplateModal
        formFilesPreviewSlot={ImagesViewer}
        isLoading={isCreating || isDeleting || isUpdating || isDeletingFile}
        title={selectedTemplate ? t("channels.updatePush") : t("channels.push")}
        onClose={handleCloseTemplateModal}
        template={values.find((template) => template.id === selectedTemplate)}
        isOpen={isOpenTemplateModal}
        buttonText={
          selectedTemplate ? t("buttons.update") : t("buttons.create")
        }
        onSubmit={handleTemplateSubmit}
      />
      {renderAddPushButton()}
    </>
  );
};

export default ChannelTemplates;
